import { useForm } from 'react-hook-form';
import { Form } from '@qodo/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGerritCreateInstallationMutation } from 'network/mutations/useGerritCreateInstallationMutation';
import { getErrorToastContent } from 'network/mutations/utils';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import {
  CONNECT_TRANSLATION_KEY,
  DEFAULT_GERRIT_API_PREFIX,
  FIELDS,
  FORM_IDS,
} from '../consts';
import { useGerritInstallation } from '../gerritInstallationContext';
import { ConnectFormData, getConnectFormSchema } from '../schemas';
import { normalizeGerritApiPrefix, normalizeGerritBaseUrl } from '../utils';

const spawnConnect = spawn(CONNECT_TRANSLATION_KEY);

export const Connect = () => {
  const { setIsPending, goNext } = useMultiStepDialog();
  const {
    setInstallationId,
    setWebhookUrl,
    setGerritHost,
    setUsername,
    setApiPrefix,
  } = useGerritInstallation();

  const methods = useForm<ConnectFormData>({
    resolver: zodResolver(getConnectFormSchema()),
    defaultValues: {
      [FIELDS.BASE_URL]: '',
      [FIELDS.USERNAME]: '',
      [FIELDS.TOKEN]: '',
      [FIELDS.API_PREFIX]: '',
    },
  });

  const { mutate: createInstallation } = useGerritCreateInstallationMutation();

  const onSubmit = (data: ConnectFormData) => {
    const normalizedBaseUrl =
      normalizeGerritBaseUrl(data[FIELDS.BASE_URL]) ?? data[FIELDS.BASE_URL];
    const username = data[FIELDS.USERNAME].trim();
    const apiPrefix = normalizeGerritApiPrefix(data[FIELDS.API_PREFIX]);

    setIsPending(true);
    createInstallation(
      {
        baseUrl: normalizedBaseUrl,
        username,
        token: data[FIELDS.TOKEN],
        // Omitted entirely when untouched, so the backend's default applies
        // rather than this form pinning a second copy of it.
        ...(apiPrefix ? { apiPrefix } : {}),
      },
      {
        onSuccess: ({ installationId, webhookUrl }) => {
          setInstallationId(installationId);
          setWebhookUrl(webhookUrl ?? undefined);
          setGerritHost(normalizedBaseUrl);
          setUsername(username);
          // The webhook-setup commands on the final step address the same
          // authenticated path, so they must use whatever was entered here.
          setApiPrefix(apiPrefix);
          setIsPending(false);
          goNext();
        },
        onError: (error) => {
          setIsPending(false);
          methods.setError('root', {
            message: getErrorToastContent(
              error,
              spawnConnect('errors.generic'),
            ),
          });
        },
      },
    );
  };

  const rootError = methods.formState.errors.root;

  return (
    <Step
      title={spawnConnect('title')}
      description={spawnConnect('description')}
    >
      <Form
        {...({ id: FORM_IDS.CONNECT } as Record<string, string>)}
        methods={methods}
        onSubmit={onSubmit}
      >
        <Form.Input
          name={FIELDS.BASE_URL}
          label={spawnConnect('base-url-label')}
          placeholder={spawnConnect('base-url-placeholder')}
          autoComplete="off"
        />
        <Form.Input
          name={FIELDS.USERNAME}
          label={spawnConnect('username-label')}
          placeholder={spawnConnect('username-placeholder')}
          autoComplete="off"
        />
        <Form.Input
          type="password"
          name={FIELDS.TOKEN}
          label={spawnConnect('token-label')}
          placeholder={spawnConnect('token-placeholder')}
          autoComplete="off"
        />
        <Form.Input
          name={FIELDS.API_PREFIX}
          label={spawnConnect('api-prefix-label')}
          placeholder={DEFAULT_GERRIT_API_PREFIX}
          description={spawnConnect('api-prefix-description')}
          autoComplete="off"
        />
        {rootError && (
          <p className="text-sm text-destructive">{rootError.message}</p>
        )}
      </Form>
    </Step>
  );
};
